import React from "react";
import { Search, Calendar, MapPin, Download, X } from "lucide-react";
import { labelCls } from "./tokens";
import ActionButton from "./ActionButton";

/**
 * SearchFilterBar — unified search + filter row used on every queue.
 * activeFilters: optional array of { key, label, onRemove } chips
 */
export default function SearchFilterBar({
  searchValue,
  onSearchChange,
  placeholder = "Search by customer, SR number, mobile…",
  locationValue,
  onLocationClick,
  locationLabel = "All Locations",
  dateLabel = "Any Date",
  onDateClick,
  statusChips = [], // [{key,label,active,onClick}]
  activeFilters = [],
  onExport,
  testId = "search-filter-bar",
}) {
  return (
    <div
      data-testid={testId}
      className="bg-onedesk-card border border-onedesk-border rounded-od-card shadow-od-subtle p-4 flex flex-col gap-4"
    >
      {/* Row 1 — Search + filter buttons + Export */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-onedesk-textSecondary"
            strokeWidth={2.25}
          />
          <input
            type="text"
            value={searchValue || ""}
            onChange={(e) => onSearchChange?.(e.target.value)}
            placeholder={placeholder}
            data-testid={`${testId}-input`}
            className="w-full rounded-od-input border border-onedesk-border bg-onedesk-card pl-9 pr-3 py-2 text-sm font-medium text-onedesk-textPrimary placeholder:text-onedesk-textSecondary focus:outline-none focus:ring-2 focus:ring-onedesk-primary/20 focus:border-onedesk-primary"
          />
        </div>

        <FilterButton
          Icon={MapPin}
          label={locationLabel}
          onClick={onLocationClick}
          testId={`${testId}-location`}
        />
        <FilterButton
          Icon={Calendar}
          label={dateLabel}
          onClick={onDateClick}
          testId={`${testId}-date`}
        />
        {onExport && (
          <ActionButton
            variant="secondary"
            size="sm"
            Icon={Download}
            onClick={onExport}
            testId={`${testId}-export`}
          >
            Export
          </ActionButton>
        )}
      </div>

      {/* Row 2 — Status chips */}
      {statusChips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className={labelCls}>Status</span>
          {statusChips.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={c.onClick}
              data-testid={`${testId}-chip-${c.key}`}
              className={`rounded-od-input border px-3 py-1 text-xs font-bold uppercase tracking-wider transition-colors ${
                c.active
                  ? "border-onedesk-primary bg-onedesk-primary text-white"
                  : "border-onedesk-border bg-onedesk-card text-onedesk-textSecondary hover:border-onedesk-primary"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      )}

      {/* Row 3 — Active filter chips with remove */}
      {activeFilters.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-t border-onedesk-border pt-3">
          <span className={labelCls}>Active</span>
          {activeFilters.map((f) => (
            <span
              key={f.key}
              data-testid={`${testId}-active-${f.key}`}
              className="inline-flex items-center gap-1.5 rounded-od-input bg-onedesk-bg border border-onedesk-border px-3 py-1 text-xs font-bold text-onedesk-textPrimary"
            >
              {f.label}
              {f.onRemove && (
                <button
                  type="button"
                  onClick={f.onRemove}
                  className="rounded-full hover:bg-onedesk-border p-0.5"
                  aria-label={`Remove ${f.label}`}
                >
                  <X className="h-3 w-3" strokeWidth={2.5} />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function FilterButton({ Icon, label, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="inline-flex items-center gap-2 rounded-od-input border border-onedesk-border bg-onedesk-card px-3 py-2 text-sm font-bold text-onedesk-textPrimary hover:border-onedesk-primary transition-colors"
    >
      <Icon className="h-4 w-4 text-onedesk-textSecondary" strokeWidth={2.25} />
      {label}
    </button>
  );
}
